import gql from 'graphql-tag';

import { Query, Mutation } from 'core/index';

import { PROJECT_CONNECTION_FIELDS } from 'apps/project/gql';
import { CONTENT_FIELDS } from 'apps/content/gql';
import { USER_ERROR_FIELDS } from 'core/errors';

export const COLLECTION_LIST_FIELDS = gql`
  fragment collectionListFields on Collection {
    id
    slug
    name
    description
    projectsCount
    images {
      id
      path
      url
    }
    content {
      id
      followersCount
      whitelabel
    }
    creatorProfile {
      id
      imageFallbackChar
      avatar {
        id
        path
        url
      }
    }
    tags {
      id
      name
    }
    space {
      id
      spaceType
      content {
        id
        slug
      }
    }
  }
`;

export const COLLECTION_FIELDS = gql`
  ${CONTENT_FIELDS}
  fragment collectionFields on Collection {
    id
    canUpdate
    canDelete
    slug
    name
    description
    projectsCount
    images {
      id
      path
      url
    }
    content {
      ...contentFields
    }
    creatorProfile {
      id
      username
      fullName
      imageFallbackChar
      avatar {
        id
        path
        url
      }
    }
    tags {
      id
      name
    }
    space {
      id
      spaceType
      content {
        id
        slug
      }
    }
  }
`;

export const COLLECTION_CONNECTION_FIELDS = gql`
  ${COLLECTION_LIST_FIELDS}
  fragment collectionConnectionFields on CollectionConnection {
    pageInfo {
      startCursor
      endCursor
      hasNextPage
    }
    totalCount
    edges {
      cursor
      node {
        ...collectionListFields
      }
    }
  }
`;

export const COLLECTION_MUTATION = gql`
  ${COLLECTION_FIELDS}
  ${USER_ERROR_FIELDS}
  mutation Collection($collectionInput: CollectionInput) {
    collection(collectionData: $collectionInput) {
      collection {
        ...collectionFields
      }
      project {
        id
        collections {
          id
        }
        starCount
        isStarred
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class CollectionMutation extends Mutation {
  static conf = {
    GQL: COLLECTION_MUTATION,
    inputName: 'collectionInput',
    resultPath: 'profile.collection',
    errorPath: 'profile.userErrors',
  };
}

export const COLLECTION = gql`
  ${COLLECTION_FIELDS}
  ${PROJECT_CONNECTION_FIELDS}
  ${USER_ERROR_FIELDS}
  query Collection(
    $space: String
    $slug: String
    $cursor: String
    $count: Int
    $sortBy: String
  ) {
    collection(space: $space, slug: $slug) {
      result {
        ...collectionFields
        projects(first: $count, after: $cursor, sortBy: $sortBy) {
          ...projectConnectionFields
        }
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class CollectionQuery extends Query {
  static conf = {
    GQL: COLLECTION,
    varNames: ['space', 'slug'],
    resultPath: 'collection.result',
    errorPath: 'collection.userErrors',
  };
}

export const PROFILE_COLLECTIONS = gql`
  ${COLLECTION_CONNECTION_FIELDS}
  ${USER_ERROR_FIELDS}
  query ProfileCollections(
    $username: String!
    $cursor: String
    $batchSize: Int
    $sortBy: String
  ) {
    profile(username: $username) {
      result(username: $username) {
        id
        collections(first: $batchSize, after: $cursor, sortBy: $sortBy) {
          ...collectionConnectionFields
        }
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class ProfileCollectionsQuery extends Query {
  static conf = {
    GQL: PROFILE_COLLECTIONS,
    varNames: ['username', 'cursor', 'sortBy', 'batchSize'],
    resultPath: 'profile.result.collections',
    errorPath: 'profile.userErrors',
  };
}

export const INITIATIVE_COLLECTIONS = gql`
  ${COLLECTION_CONNECTION_FIELDS}
  ${USER_ERROR_FIELDS}
  query InitiativeCollections(
    $slug: String
    $cursor: String
    $batchSize: Int
    $sortBy: String
  ) {
    initiative(slug: $slug) {
      result {
        id
        collections(first: $batchSize, after: $cursor, sortBy: $sortBy) {
          ...collectionConnectionFields
        }
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class InitiativeCollectionsQuery extends Query {
  static conf = {
    GQL: INITIATIVE_COLLECTIONS,
    varNames: ['slug', 'cursor', 'sortBy', 'batchSize'],
    resultPath: 'initiative.result.collections',
  };
}

export const COLLECTION_PROJECTS = gql`
  ${PROJECT_CONNECTION_FIELDS}
  ${USER_ERROR_FIELDS}
  query CollectionProjects(
    $space: String
    $slug: String
    $cursor: String
    $batchSize: Int
    $sortBy: String
  ) {
    collection(space: $space, slug: $slug) {
      result {
        id
        projects(first: $batchSize, after: $cursor, sortBy: $sortBy) {
          ...projectConnectionFields
        }
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class CollectionProjectsQuery extends Query {
  static conf = {
    GQL: COLLECTION_PROJECTS,
    varNames: ['space', 'slug', 'cursor', 'sortBy', 'batchSize'],
    resultPath: 'collection.result.projects',
    errorPath: 'collection.userErrors',
  };
}

const COLLECTIONS = gql`
  ${COLLECTION_CONNECTION_FIELDS}
  ${USER_ERROR_FIELDS}
  query Collections($batchSize: Int, $cursor: String, $sortBy: String) {
    collections(first: $batchSize, after: $cursor, sortBy: $sortBy) {
      result {
        ...collectionConnectionFields
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class CollectionsQuery extends Query {
  static conf = {
    GQL: COLLECTIONS,
    varNames: ['batchSize', 'cursor', 'sortBy'],
    resultPath: 'collections.result',
    errorPath: 'collections.userErrors',
  };
}
